import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, X, Users } from "lucide-react";
import { useChaseDeckList, removeChase } from "../lib/chase";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { useCurrency } from "../context/CurrencyContext";

export default function ChaseDeckStrip() {
  const items = useChaseDeckList();
  const currencyCtx = useCurrency();
  const fmt = currencyCtx?.format || ((v) => (v == null ? null : `$${v.toFixed(2)}`));

  const remove = async (id) => {
    const ok = await removeChase(id);
    if (ok) toast.success("Removed from Chase Deck");
    else toast.error("Couldn't remove it");
  };

  if (!items) return null;

  if (items.length === 0) {
    return (
      <section data-testid="binder-chase-deck-empty" className="mb-8 border border-dashed border-pink-400/20 bg-pink-500/[0.03] rounded-2xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-zinc-400 flex items-center gap-2">
          <Heart className="w-4 h-4 text-pink-400" /> Start building your ChaseDeck to track cards you want.
        </p>
        <Link to="/community" data-testid="chase-empty-cta" className="text-xs font-medium px-3 py-1.5 rounded-full border border-pink-400/30 text-pink-300 hover:bg-pink-500/10 transition-colors">
          Explore cards →
        </Link>
      </section>
    );
  }

  return (
    <section data-testid="binder-chase-deck" className="mb-8 border border-pink-400/20 bg-pink-500/5 rounded-2xl p-4">
      <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mb-1">
        <Heart className="w-4 h-4 text-pink-400 fill-pink-400" /> Chase Deck
        <span className="text-xs text-zinc-500 font-normal">({items.length})</span>
      </h2>
      <p className="text-xs text-zinc-500 mb-3">
        Cards you're hunting — shown on your public binder so other collectors can ping you a trade.
        Add more from <Link to="/community" className="text-[#A78BFA] hover:underline">Explore</Link>.
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {items.map((item) => (
          <div key={item.id} data-testid={`chase-item-${item.id}`} className="w-28 shrink-0 relative group">
            {item.image_url ? (
              <img
                src={pcImageUrl(item.image_url.startsWith("http") ? item.image_url : `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`, "240")}
                onError={pcImageFallback}
                alt={item.name}
                className="w-28 aspect-[3/4] object-cover rounded-lg border border-white/10"
              />
            ) : (
              <div className="w-28 aspect-[3/4] flex items-center justify-center text-[9px] text-zinc-600 border border-white/10 rounded-lg text-center px-1">{item.name}</div>
            )}
            <button
              data-testid={`chase-remove-${item.id}`}
              onClick={() => remove(item.id)}
              title="Remove from Chase Deck"
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-white transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
            <p className="text-[10px] text-zinc-400 mt-1 truncate">{item.name}</p>
            {item.est_value != null && (
              <p
                data-testid={`chase-value-${item.id}`}
                title="Estimated market value — based on collectors' priced copies"
                className="text-[10px] font-mono-plex font-semibold text-[#A78BFA]"
              >
                ~{fmt(item.est_value)}
              </p>
            )}
            {item.owner_count > 0 && (
              <Link
                to="/community"
                data-testid={`chase-owners-${item.id}`}
                className="text-[9px] text-emerald-400 hover:underline flex items-center gap-1"
              >
                <Users className="w-2.5 h-2.5" /> {item.owner_count} collector{item.owner_count > 1 ? "s" : ""} ha{item.owner_count > 1 ? "ve" : "s"} this
              </Link>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
